import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEdit, faTrash, faFilePen } from "@fortawesome/free-solid-svg-icons";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomTable from "../../Components/CustomTable";
import CustomModal from "../../Components/CustomModal";
import TableDropdown from "../../Components/TableDropdown";
import CustomPagination from "../../Components/CustomPagination"
import CustomInput from "../../Components/CustomInput";
import CustomButton from "../../Components/CustomButton";
import "./style.css";
import { useDelete, useGet } from "../../Api";
import { useNotification } from '../../Components/GlobalNotification';

export const DraftListingManagement = () => {
  const { showNotification } = useNotification();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [delId, setDelId] = useState(null);

  // Use backend filter=drafts so only draft listings (status=0) are returned
  const { ApiData: UseeListingData, loading: UseeListingLoading, error: UseeListingError, get: GetUseeListing } = useGet(`/listings?page=${currentPage}&filter=drafts${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`);
  const { ApiData: DelListData, loading: DelListLoading, error: DelListError, del: GetDelList } = useDelete(`/listings/${delId}`);

  const navigate = useNavigate();

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Process API response — backend already filters to draft only
  useEffect(() => {
    if (!UseeListingData) return;

    let listings = [];
    let perPage = 10;
    let total = 0;

    if (UseeListingData?.data?.data && Array.isArray(UseeListingData.data.data)) {
      // Paginated response: { data: { data: [...], per_page: N, total: N } }
      listings = UseeListingData.data.data;
      perPage = UseeListingData.data.per_page || 10;
      total = UseeListingData.data.total || listings.length;
    } else if (Array.isArray(UseeListingData?.data)) {
      // Direct array response
      listings = UseeListingData.data;
      total = listings.length;
    }

    setData(listings);
    setFilteredData(listings);
    setItemsPerPage(perPage);
    setTotalItems(total);
  }, [UseeListingData]);

  const handleRemove = (del) => {
    setDelId(del);
  };

  useEffect(() => {
    if (delId != null) {
      GetDelList();
    }
  }, [delId]);

  useEffect(() => {
    if (DelListData) {
      showNotification({ type: 'info', title: 'Information', message: DelListData?.message });
      GetUseeListing();
      setDelId(null);
    }
  }, [DelListData]);

  const hanldeRoute = () => {
    navigate('/add-listing');
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleRetry = () => {
    setData([]);
    setFilteredData([]);
    GetUseeListing(`/listings?page=${currentPage}&filter=drafts${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`);
  };

  // Re-fetch when page changes (also runs on mount)
  useEffect(() => {
    document.title = 'Kvr Draft Listings';
    GetUseeListing(`/listings?page=${currentPage}&filter=drafts${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`);
  }, [currentPage]);

  // Search runs on the server (all pages): wait until typing stops, then go back to page 1
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(inputValue.trim()), 400);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const searchMounted = useRef(false);
  useEffect(() => {
    if (!searchMounted.current) { searchMounted.current = true; return; }
    if (currentPage !== 1) setCurrentPage(1);
    else GetUseeListing(`/listings?page=1&filter=drafts${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`);
  }, [searchTerm]);


  const upcomingHeader = [
    { key: "id",        title: "S.No"      },
    { key: "Thumbnail", title: "Thumbnail" },
    { key: "Title",     title: "Title"     },
    { key: "Address",   title: "Address"   },
    { key: "Type",      title: "Type"      },
    { key: "Status",    title: "Status"    },
    { key: "action",    title: "Action"    },
  ];

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <div className="row mb-3 justify-content-between">
                  <div className="col-md-6 mb-2">
                    <h2 className="mainTitle">Draft Listings</h2>
                    <small className="text-muted">Manage incomplete listings saved for later</small>
                  </div>
                  <div className="col-md-6 mb-2">
                    <div className="addUser">
                      <CustomButton text="Add New Listing" variant='primaryButton' onClick={hanldeRoute} />
                      <CustomInput type="text" placeholder="Search Here..." value={inputValue} inputClass="mainInput" onChange={handleChange} />
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-12">
                    <CustomTable headers={upcomingHeader}>
                      <tbody>
                        {UseeListingLoading ? (
                          <tr>
                            <td colSpan={upcomingHeader.length} className="text-center py-4">
                              <div className="text-muted">
                                <div className="spinner-border spinner-border-sm me-2" role="status" />
                                Loading draft listings...
                              </div>
                            </td>
                          </tr>
                        ) : UseeListingError ? (
                          <tr>
                            <td colSpan={upcomingHeader.length} className="text-center py-4">
                              <div className="text-danger">
                                <p>Failed to load listings. Please refresh the page.</p>
                                <button type="button" className="btn btn-sm btn-primary mt-2" onClick={handleRetry}>
                                  Retry
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : filteredData.length === 0 ? (
                          <tr>
                            <td colSpan={upcomingHeader.length} className="text-center py-4">
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faFilePen} size="2x" className="mb-2 d-block mx-auto" />
                                <p className="mb-1 fw-semibold">No draft listings found</p>
                                <small>Listings saved with &ldquo;Save for Later&rdquo; will appear here</small>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          filteredData.map((item, index) => (
                            <tr key={item?.id ?? index}>
                              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td className="text-capitalize">
                                {item?.media?.[0]?.image_url || item?.linked_media?.[0]?.image_url ? (
                                  <img 
                                    src={item.media?.[0]?.image_url || item.linked_media?.[0]?.image_url} 
                                    className="mw-100 listingImagePreview" 
                                    alt="thumbnail" 
                                  />
                                ) : (
                                  <div className="bg-light d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', borderRadius: '4px' }}>
                                    <small className="text-muted">No Image</small>
                                  </div>
                                )}
                              </td>
                              <td className="fw-bold">{item?.title || 'Untitled'}</td>
                              <td>
                                {item?.location?.city
                                  ? `${item.location.city}, ${item.location.country}`
                                  : 'Location not set'}
                              </td>
                              <td>{item?.type_of_listing?.listing_name || '-'}</td>
                              <td>
                                <span className="badge bg-warning text-dark">Draft</span>
                              </td>
                              <td>
                                <TableDropdown>
                                  <Link to={`/listing-management/edit-listing/${item?.id}`} className="tableAction">
                                    <FontAwesomeIcon icon={faEdit} className="tableActionIcon" />Edit
                                  </Link>
                                  <Link to={`/listing-management/listing-details/${item?.id}`} className="tableAction">
                                    <FontAwesomeIcon icon={faEye} className="tableActionIcon" />View
                                  </Link>
                                  <button type="button" className="tableAction" onClick={() => { handleRemove(item?.id); }}>
                                    <FontAwesomeIcon icon={faTrash} className="tableActionIcon" />Remove
                                  </button>
                                </TableDropdown>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </CustomTable>
                    {(
                      <CustomPagination
                        itemsPerPage={itemsPerPage}
                        totalItems={totalItems}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <CustomModal
            show={showModal}
            close={() => { setShowModal(false); }}
            success
            heading='Draft listing deleted successfully'
          />
        </div>
      </DashboardLayout>
    </>
  );
};
